import { Badge } from '@/components/ui/shadcn/badge';
import { useTranslations } from '@/hooks/useTranslations';
import { taskDaysToDue, taskScheduleTone, type TaskScheduleTone } from '@/lib/projects/task-schedule';
import { settingLabel } from '@/lib/status-labels';
import { cn } from '@/lib/utils';
import type { ProjectTask } from '@/types';

/**
 * Task status as a Fluent state badge.
 *
 * The label still comes from the settings catalogue (so a status an admin adds is
 * localised without a code change), but the colour does not: those rows ship the
 * legacy palette classes (`bg-green-100 …`) the design system forbids, so the
 * status maps to a semantic token here instead.
 */
const STATUS_CLASSES: Record<string, string> = {
    'not-started': 'bg-muted text-muted-foreground border-border',
    'in-progress': 'bg-info-subtle text-info-subtle-foreground border-transparent',
    'on-hold': 'bg-warning-subtle text-warning-subtle-foreground border-transparent',
    completed: 'bg-success-subtle text-success-subtle-foreground border-transparent',
    blocked: 'bg-danger-subtle text-danger-subtle-foreground border-transparent',
    cancelled: 'bg-muted text-muted-foreground border-border',
};

const FALLBACK_CLASSES = 'bg-muted text-muted-foreground border-border';

/** Stored values come in both `in-progress` and `in_progress` shapes. */
function statusKey(status: string): string {
    return status.replaceAll('_', '-').toLowerCase();
}

export function TaskStatusBadge({ status, className }: { status: string | null | undefined; className?: string }) {
    const { t } = useTranslations();

    if (!status) {
        return <span className="text-muted-foreground">—</span>;
    }

    const value = String(status);

    return (
        <Badge variant="outline" className={cn(STATUS_CLASSES[statusKey(value)] ?? FALLBACK_CLASSES, className)}>
            {settingLabel(t, 'project_task_statuses', value)}
        </Badge>
    );
}

const SCHEDULE_CLASSES: Record<Exclude<TaskScheduleTone, 'none'>, string> = {
    overdue: 'bg-danger-subtle text-danger-subtle-foreground border-transparent',
    due_today: 'bg-warning-subtle text-warning-subtle-foreground border-transparent',
    due_soon: 'bg-warning-subtle text-warning-subtle-foreground border-transparent',
    on_track: 'bg-muted text-muted-foreground border-border',
};

/**
 * Where a task stands against its end date — overdue, due soon, or comfortable.
 * Saying it outright is what stops the reader comparing two dates in their head.
 */
export function TaskScheduleBadge({ task, className }: { task: ProjectTask; className?: string }) {
    const { t } = useTranslations();
    const tone = taskScheduleTone(task);

    if (tone === 'none') {
        return <span className="text-muted-foreground">—</span>;
    }

    const days = taskDaysToDue(task) ?? 0;
    const labels: Record<Exclude<TaskScheduleTone, 'none'>, string> = {
        overdue: t('projects.tables.tasks.overdue', 'Overdue by :days d.').replace(':days', String(Math.abs(days))),
        due_today: t('projects.tables.tasks.due_today', 'Due today'),
        due_soon: t('projects.tables.tasks.due_in', 'In :days d.').replace(':days', String(days)),
        on_track: t('projects.tables.tasks.due_in', 'In :days d.').replace(':days', String(days)),
    };

    return (
        <Badge variant="outline" className={cn(SCHEDULE_CLASSES[tone], className)}>
            {labels[tone]}
        </Badge>
    );
}
